import { type InfiniteData, keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import { departmentRosterQueryOptions } from "../../../hooks/useNurses";
import { backendService } from "../../../services/backend.service";
import { formatPersonFullName } from "../../../utils/formatters";
import {
	type FacilityAnnouncementAudience,
	type FacilityAnnouncementResult,
	MessageStatus,
	type Nurse,
	type PostStaffMessageRequest,
	type StaffCommunicationHistoryResponse,
	type StaffCommunicationSummaryResponse,
	type UserSentMessage,
} from "../../../utils/models";

const getStaffCommunicationThreadsQueryKey = (departmentId: string) => ["staffCommunicationThreads", departmentId];
const getStaffCommunicationThreadQueryKey = (departmentId: string, userId: string) => ["staffCommunicationThread", departmentId, userId];
const THREAD_HISTORY_PAGE_SIZE = 100;
const STAFF_COMMUNICATION_REFETCH_INTERVAL_MS = 120_000;

type StaffCommunicationSummaryQueryData = StaffCommunicationSummaryResponse;

export const useThreadsQuery = (departmentId: string) => {
	return useQuery<StaffCommunicationSummaryQueryData>({
		queryKey: getStaffCommunicationThreadsQueryKey(departmentId),
		queryFn: async () => {
			const response = await backendService.fetchStaffCommunicationThreadSummaries(departmentId);

			// Convert date strings to luxon objects
			const smsWithDates = response.data.sms.map((msg: UserSentMessage) => ({
				...msg,
				dateCreated: msg.dateCreated,
			}));

			const pushWithDates = response.data.push.map((msg) => ({
				...msg,
				dateSent: msg.dateSent,
			}));

			return {
				sms: smsWithDates,
				push: pushWithDates,
				unreadCounts: response.data.unreadCounts,
			};
		},
		refetchInterval: STAFF_COMMUNICATION_REFETCH_INTERVAL_MS,
		throwOnError: (error: unknown) => {
			console.warn("Staff communication threads fetch failed (suppressed toast):", error);
			return false;
		},
		gcTime: 0, // replaces keepPreviousData: false
		enabled: !!departmentId, // Prevents query if no ID is provided
	});
};

export const useThreadMessagesQuery = (departmentId: string, userId: string | undefined) => {
	return useInfiniteQuery({
		queryKey: getStaffCommunicationThreadQueryKey(departmentId, userId ?? ""), // DEFAULT: disabled queries need a stable key before a nurse is selected.
		queryFn: async ({ pageParam }) => {
			if (!userId) throw new Error("A user must be selected before loading message history");
			const response = await backendService.fetchStaffCommunicationThreadHistory(departmentId, userId, THREAD_HISTORY_PAGE_SIZE, pageParam);
			return response.data;
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? allPages.length * THREAD_HISTORY_PAGE_SIZE : undefined),
		refetchInterval: STAFF_COMMUNICATION_REFETCH_INTERVAL_MS,
		enabled: Boolean(departmentId && userId),
		throwOnError: (error: unknown) => {
			console.warn("Staff communication thread messages fetch failed (suppressed toast):", error);
			return false;
		},
	});
};

export const usePostStaffMessage = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const {
		chosenDepartment: { department_id: chosenDepartmentId },
	} = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const lastNameFirst = useLastNameFirst();
	const { trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { userInfo } = useAuth();

	return useMutation({
		mutationFn: async ({ message, userIds, addAppLink }: PostStaffMessageRequest) => {
			const res = await backendService.postStaffCommunicationMessage(chosenDepartmentId, userIds, message, addAppLink);
			await new Promise((resolve) => setTimeout(resolve, 7_000));
			const sids = res.data.flatMap((sentMessage) => (sentMessage.success && sentMessage.sid ? [sentMessage.sid] : []));
			// DEFAULT: a failed, non-critical delivery-status refresh must not turn a successful send into a failed send.
			let statusMessages: UserSentMessage[] = [];
			try {
				statusMessages = (await backendService.getMessagesStatuses(chosenDepartmentId, sids)).data;
			} catch (error) {
				console.warn("Staff communication status refresh failed (suppressed toast):", error);
			}
			return { res, statusMessages };
		},
		throwOnError: false,
		onMutate: async ({ userIds, message, tempId }) => {
			await queryClient.cancelQueries({
				queryKey: getStaffCommunicationThreadsQueryKey(chosenDepartmentId),
			});

			const previousThreads = queryClient.getQueryData<StaffCommunicationSummaryQueryData>(getStaffCommunicationThreadsQueryKey(chosenDepartmentId));

			userIds.forEach((userId) => {
				const newMessage: UserSentMessage = {
					body: message,
					dateCreated: DateTime.now(),
					tempId,
					departmentId: chosenDepartmentId,
					userId,
					senderUserId: userInfo.id,
					id: 0,
					status: MessageStatus.sending,
					direction: "outbound",
					senderFirstName: userInfo.first_name,
				};

				queryClient.setQueryData<StaffCommunicationSummaryQueryData>(getStaffCommunicationThreadsQueryKey(chosenDepartmentId), (old) =>
					old ? { ...old, sms: [newMessage, ...old.sms.filter((item) => item.userId !== userId)] } : old,
				);
				queryClient.setQueryData<InfiniteData<StaffCommunicationHistoryResponse>>(getStaffCommunicationThreadQueryKey(chosenDepartmentId, userId), (old) => {
					if (!old?.pages[0]) return old;
					const [firstPage, ...remainingPages] = old.pages;
					return { ...old, pages: [{ ...firstPage, sms: [newMessage, ...firstPage.sms] }, ...remainingPages] };
				});
			});

			return { previousThreads, userIds, tempId };
		},
		onError: (error, _variables, context) => {
			queryClient.setQueryData(getStaffCommunicationThreadsQueryKey(chosenDepartmentId), context?.previousThreads);
			void queryClient.invalidateQueries({ queryKey: ["staffCommunicationThread", chosenDepartmentId] });
			showErrorSnackbar("Failed to post message", { error });
		},
		onSuccess: async ({ res, statusMessages }, { userIds }) => {
			if (res.data.filter((m) => m.success).length === 0) {
				showErrorSnackbar("Failed to send message", { userIds });
			}
			const failedMessages = statusMessages.filter((msg) => msg.status === MessageStatus.failed);
			void queryClient.invalidateQueries({ queryKey: getStaffCommunicationThreadsQueryKey(chosenDepartmentId) });
			void queryClient.invalidateQueries({ queryKey: ["staffCommunicationThread", chosenDepartmentId] });

			if (userIds.length > 1 /* Means we are broadcasting */ && failedMessages.length) {
				const failedUsers = failedMessages.map((m) => m.userId);
				// The roster is read (or loaded) when the result arrives rather than subscribing every sender to it.
				let roster: Nurse[];
				try {
					roster = await queryClient.ensureQueryData(departmentRosterQueryOptions(chosenDepartmentId));
				} catch (error) {
					trackError("staff_message_failed_recipients_roster_error", { department_id: chosenDepartmentId, error: String(error) });
					showSnackbar(t("messages.send.failed.unnamed", { count: failedUsers.length }), "warning");
					return;
				}
				const failedUserNames = roster.filter(({ userId }) => userId && failedUsers.includes(userId)).map((n) => formatPersonFullName(n, lastNameFirst));

				showSnackbar(
					failedUserNames.length > 0
						? t("messages.send.failed", { failedUsers: failedUserNames.join(", ") })
						: t("messages.send.failed.unnamed", { count: failedUsers.length }),
					"warning",
				);
			}
		},
	});
};

export const useUpdateThreadReadStatus = (departmentId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (threadUserId: string) => {
			const response = await backendService.updateThreadReadStatus(departmentId, threadUserId);
			return response.data;
		},
		onSuccess: (_, threadUserId) => {
			// Optimistically update the cache
			queryClient.setQueryData<StaffCommunicationSummaryQueryData>(getStaffCommunicationThreadsQueryKey(departmentId), (old) => {
				if (!old) return old;
				return {
					...old,
					unreadCounts: {
						...old.unreadCounts,
						[threadUserId]: 0,
					},
				};
			});
		},
		throwOnError: false,
	});
};

/** The snackbar line for a facility announcement: who got it, then how many numbers couldn't be texted. */
const facilityAnnouncementSummary = (result: FacilityAnnouncementResult, t: TFunction): string => {
	const sent = t("broadcast.facility.sent", { count: result.sent, facility: result.facilityName });
	return result.failed > 0 ? `${sent} ${t("broadcast.facility.failed", { count: result.failed })}` : sent;
};

const ANNOUNCEMENTS_PAGE_SIZE = 20;
const getFacilityAnnouncementsQueryKey = (departmentId: string) => ["facilityAnnouncements", departmentId];

export type FacilityAnnouncementRequest = { message: string; audience: FacilityAnnouncementAudience };

/** Text everyone active in the chosen department's building, or chosen departments of it. The server picks the recipients. */
export const usePostFacilityAnnouncement = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const {
		chosenDepartment: { department_id: chosenDepartmentId },
	} = useChosenDepartment();
	const { showSnackbar } = useSnackbar();
	const showErrorSnackbar = useErrorSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async ({ message, audience }: FacilityAnnouncementRequest) =>
			(await backendService.postFacilityAnnouncement(chosenDepartmentId, message, audience)).data,
		throwOnError: false,
		onMutate: ({ audience }) => {
			track("facility_announcement_start", { department_id: chosenDepartmentId, whole_building: audience.departmentIds === null });
		},
		onError: (error) => {
			trackError("facility_announcement_error", { department_id: chosenDepartmentId, error: String(error) });
			// A refusal the server explains (no permission, feature off, a department outside the building) shows its
			// own translated reason; anything else — the request may have gone out — keeps the don't-resend warning.
			showErrorSnackbar(t("broadcast.facility.error"), { department_id: chosenDepartmentId }, error);
		},
		onSuccess: (result) => {
			track("facility_announcement_success", {
				department_id: chosenDepartmentId,
				sent: result.sent,
				failed: result.failed,
			});
			showSnackbar(facilityAnnouncementSummary(result, t), result.failed > 0 ? "warning" : "success");
			void queryClient.invalidateQueries({ queryKey: getStaffCommunicationThreadsQueryKey(chosenDepartmentId) });
			void queryClient.invalidateQueries({ queryKey: ["staffCommunicationThread", chosenDepartmentId] });
			void queryClient.invalidateQueries({ queryKey: getFacilityAnnouncementsQueryKey(chosenDepartmentId) });
		},
	});
};

/** The announcements of the department's building, newest first, a page at a time. Delivery counts follow the
 *  carriers' reports, so the list refreshes on the chat's interval. */
export const useFacilityAnnouncements = (departmentId: string) => {
	const { trackError } = useAnalytics();
	return useInfiniteQuery({
		queryKey: getFacilityAnnouncementsQueryKey(departmentId),
		queryFn: async ({ pageParam }) => {
			try {
				return (await backendService.getFacilityAnnouncements(departmentId, ANNOUNCEMENTS_PAGE_SIZE, pageParam)).data;
			} catch (error) {
				trackError("facility_announcements_fetch_error", { department_id: departmentId, error: String(error) });
				throw error;
			}
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? allPages.length * ANNOUNCEMENTS_PAGE_SIZE : undefined),
		refetchInterval: STAFF_COMMUNICATION_REFETCH_INTERVAL_MS,
		// The list shows its own error state with a retry, so no global toast.
		throwOnError: false,
		enabled: Boolean(departmentId),
	});
};

/** How many texts an announcement with this audience would send, for the composer's counts. The last counts stay
 *  on screen while new ones load, so the numbers don't blink as the choices change. */
export const useFacilityAnnouncementAudience = (departmentId: string, audience: FacilityAnnouncementAudience, enabled: boolean) => {
	const { trackError } = useAnalytics();
	return useQuery({
		queryKey: ["facilityAnnouncementAudience", departmentId, audience],
		queryFn: async () => {
			try {
				return (await backendService.countFacilityAnnouncementAudience(departmentId, audience)).data;
			} catch (error) {
				trackError("facility_announcement_audience_fetch_error", { department_id: departmentId, error: String(error) });
				throw error;
			}
		},
		enabled,
		placeholderData: keepPreviousData,
		// The composer shows the counts as unknown instead, so no global toast.
		throwOnError: false,
	});
};
